import { NmLoader } from '../../components/NmLoader';
import { MobileSubscriptionSettingsPage } from './MobileSubscriptionSettingsPage';
import { useAppBack } from '../../hooks/useAppBack';
import React, { useState } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  IoSparklesOutline,
  IoBarChartOutline,
  IoGlobeOutline,
  IoLinkOutline,
  IoMoonOutline,
  IoNotificationsOutline,
  IoPersonOutline,
  IoPulseOutline,
  IoSchoolOutline,
  IoCardOutline,
} from 'react-icons/io5';
import { useAuth } from '../../hooks/useAuth';
import { NmButton } from '../../components/NmButton';
import { NmCard } from '../../components/NmCard';
import { NmEmptyState } from '../../components/NmEmptyState';
import { NmMenuCard, NmMenuRow } from '../../components/NmMenuList';
import { NmMenuIcon } from '../../components/NmMenuIcon';
import { AddCoachModal } from '../../components/AddCoachModal';
import {
  AthleteSettingsModal,
  type AthleteSettingsView,
} from '../../components/athlete-settings/AthleteSettingsModal';
import { MobileLanguageSettingsPage } from './MobileLanguageSettingsPage';
import { MobileNotificationSettingsPage } from './MobileNotificationSettingsPage';
import { MobileAppearanceSettingsPage } from './MobileAppearanceSettingsPage';
import { useAthleteContext } from '../../context/AthleteContext';
import { useLocale } from '../../hooks/useLocale';
import { useTheme } from '../../context/ThemeContext';
import { themeLabelKeys } from '../../components/ThemePicker';

function SettingsHome() {
  const { t } = useTranslation();
  const auth = useAuth();
  const navigate = useNavigate();
  const { effectiveUserId, isCoach } = useAthleteContext();
  const { locale, labels } = useLocale();
  const { preference } = useTheme();
  const athleteId = effectiveUserId ?? auth.user?.id ?? null;
  const viewingOtherAccount = athleteId !== auth.user?.id;
  const [showAddCoach, setShowAddCoach] = useState(false);

  return (
    <div className="mobile-screen mobile-settings-screen">
      <h1 className="nm-section-title" style={{ margin: '0 0 12px' }}>{t('tabs.settings')}</h1>

      <NmCard variant="raised-sm" style={{ marginBottom: 16 }}>
        <div style={{ fontWeight: 700, fontSize: 16 }}>{auth.user?.name ?? auth.user?.full_name}</div>
        <div style={{ fontSize: 13, color: 'var(--fg-3)', marginTop: 4 }}>{auth.user?.mobile}</div>
        {isCoach && (
          <div style={{ fontSize: 12, color: 'var(--fg-4)', marginTop: 6 }}>{t('settings.coachAccount', 'Coach account')}</div>
        )}
        {isCoach && viewingOtherAccount && <NmButton variant="ghost" size="sm" onClick={() => navigate('/settings/account')}>{t('athleteSettings.menu.account')}</NmButton>}
      </NmCard>

      <NmMenuCard>
        <NmMenuRow icon={<NmMenuIcon icon={IoCardOutline} tone="green" />}
          label={t('subscription.title')}
          description={t(auth.user?.is_subscription_active ? 'subscription.active' : 'subscription.menuHint')}
          onClick={() => navigate('/settings/subscription')} last />
      </NmMenuCard>

      {athleteId != null && (
        <NmMenuCard>
          {!viewingOtherAccount && <NmMenuRow
            icon={<NmMenuIcon icon={IoPersonOutline} tone="pink" />}
            label={t('athleteSettings.menu.account')}
            description={t('athleteSettings.menu.accountDesc')}
            onClick={() => navigate('/settings/account')}
          />}
          <NmMenuRow
            icon={<NmMenuIcon icon={IoPulseOutline} tone="green" />}
            label={t('athleteSettings.menu.zones')}
            description={t('athleteSettings.menu.zonesDesc')}
            onClick={() => navigate('/settings/zones')}
          />
          <NmMenuRow
            icon={<NmMenuIcon icon={IoBarChartOutline} tone="cyan" />}
            label={t('athleteSettings.menu.metrics')}
            description={t('athleteSettings.menu.metricsDesc')}
            onClick={() => navigate('/settings/metrics')}
          />
          <NmMenuRow
            icon={<NmMenuIcon icon={IoLinkOutline} tone="gold" />}
            label={t('athleteSettings.menu.connections')}
            description={t('athleteSettings.menu.connectionsDesc')}
            onClick={() => navigate('/settings/connections')}
            last
          />
        </NmMenuCard>
      )}

      <NmMenuCard>
        <NmMenuRow
          icon={<NmMenuIcon icon={IoMoonOutline} tone="neutral" />}
          label={t('settings.appearance', 'Appearance')}
          description={t(themeLabelKeys[preference])}
          onClick={() => navigate('/settings/appearance')}
        />
        <NmMenuRow
          icon={<NmMenuIcon icon={IoGlobeOutline} tone="neutral" />}
          label={t('settings.language', 'Language')}
          description={labels[locale]}
          onClick={() => navigate('/settings/language')}
        />
        <NmMenuRow
          icon={<NmMenuIcon icon={IoNotificationsOutline} tone="pink" />}
          label={t('tabs.notifications', 'Notifications')}
          onClick={() => navigate('/notifications')}
          last
        />
      </NmMenuCard>

      {isCoach && auth.user?.id === 5 && <NmMenuCard><NmMenuRow icon={<NmMenuIcon icon={IoSparklesOutline} tone="cyan" />} label={t('assistant.title')} description={t('assistant.subtitle')} onClick={() => navigate('/ai-assistant')} last /></NmMenuCard>}

      {!isCoach && (
        <NmMenuCard>
          <NmMenuRow
            icon={<NmMenuIcon icon={IoSchoolOutline} tone="green" />}
            label={t('app.addCoach.title')}
            description={t('app.addCoach.hint')}
            onClick={() => setShowAddCoach(true)}
            last
          />
        </NmMenuCard>
      )}

      <NmButton
        variant="danger"
        style={{ width: '100%' }}
        onClick={() => void auth.logout().then(() => navigate('/login'))}
      >
        {t('settings.logout')}
      </NmButton>

      {showAddCoach && (
        <AddCoachModal
          onClose={() => setShowAddCoach(false)}
          onSent={() => void auth.refreshProfile()}
          isCoached={auth.user?.is_coached === true}
        />
      )}
    </div>
  );
}

function SettingsDetailRoute({ view }: { view: AthleteSettingsView }) {
  const { t } = useTranslation();
  const auth = useAuth();
  const goBack = useAppBack('/settings');
  const { effectiveUserId, isCoach, athletes } = useAthleteContext();
  const athleteId = view === 'account' ? auth.user?.id ?? null : effectiveUserId;

  // A coach's own account must not be used while athlete selection hydrates.
  if (view !== 'account' && isCoach && athleteId == null && athletes.length > 0) {
    return <div className="mobile-screen mobile-settings-screen" role="status"><NmLoader size="sm" inline label={t('common.loading')} /></div>;
  }

  if (athleteId == null) {
    return (
      <NmEmptyState actionLabel={t('common.back', 'Back')} onAction={goBack}>
        {t('app.coachGate.empty')}
      </NmEmptyState>
    );
  }

  return (
    <AthleteSettingsModal
      athleteId={athleteId}
      presentation="page"
      initialView={view}
      onClose={goBack}
      onSaved={() => void auth.refreshProfile()}
    />
  );
}

export function SettingsPage() {
  const { t } = useTranslation();

  return (
    <Routes>
      <Route index element={<SettingsHome />} />
      <Route path="account" element={<SettingsDetailRoute view="account" />} />
      <Route path="zones" element={<SettingsDetailRoute view="zones-menu" />} />
      <Route path="metrics" element={<SettingsDetailRoute view="metrics" />} />
      <Route path="connections" element={<SettingsDetailRoute view="connections" />} />
      <Route path="language" element={<MobileLanguageSettingsPage />} />
      <Route path="appearance" element={<MobileAppearanceSettingsPage />} />
      <Route path="subscription" element={<MobileSubscriptionSettingsPage />} />
      <Route path="notifications" element={<MobileNotificationSettingsPage />} />
      <Route
        path="*"
        element={<NmEmptyState>{t('app.pageNotFound')}</NmEmptyState>}
      />
    </Routes>
  );
}
